import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { IoChevronBackSharp, IoChevronForwardSharp } from 'react-icons/io5';
import Navbar from '../../navbar';
import Header from '../../header';
import { fetchApplicationWithId, updateApplication, isThunkFulfilled } from '../../../store/reducers/applicationSlice';
import FormLoader from '../../forms/formLoader';
import classes from './style.module.css';
import { getstate } from '../../../store/reducers/stateSlice';
import { fetchCategories } from '../../../store/reducers/categoriesSlice';
import { getNotificationTemplate } from '../../../utility/notificationTemplates';
import { createNotification } from '../../../store/reducers/notificationSlice';
import {
    setFormFieldData,
    hideAllPagesExceptCurrentFormPage,
    goToFormNextPage,
    goToFormPreviousPage,
    setPage,
} from '../../../utility/utility';
import Index from './descripancie';
import { getSignature } from '../../../store/reducers/fileSlice';
import { hasRole, ROLES } from '../../../utility/rbac';
import RejectDialog from './rejectDialog/rejectDialog';
import StatusConfirmDialog, {
    STATUS_LABELS,
    getStatusRoute,
} from './statusConfirmDialog/statusConfirmDialog';
import AuditTrailModal from './auditTrail/AuditTrailModal';
import {
    formatLifecycleDate,
    getApprovalDate,
    getRejectionDate,
    getSubmissionDate,
} from '../../../utility/lifecycleDates';

const Descrepancies = () => {
    const dispatch = useDispatch();
    const params = useParams();
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth.user);
    const authToken = user.access_token;
    const { currentApp, fetchLoading } = useSelector((state) => state.applications);
    const applicationdata = useSelector((state) => state.applications.currentApp);
    const status = useSelector((state) => state.status.status);
    const [formData, setFormData] = useState([]);
    const [pageIds, setPageIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoaded, setIsLoaded] = useState(false);
    const [formIds, setFormIds] = useState([]);
    const [selectedFieldData, setSelectedFieldData] = useState({});
    const [signatureImage, setSignatureImage] = useState('');
    const [inputPage, setInputPage] = useState(currentPage);
    const [isDataPopulated, setIsDataPopulated] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [selectedRejectItem, setSelectedRejectItem] = useState(null);
    const [showAuditTrail, setShowAuditTrail] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [pendingStatusName, setPendingStatusName] = useState(null);

    // keep page input synced
    useEffect(() => {
        setInputPage(currentPage);
    }, [currentPage]);

    // fetch app + states + categories
    const statusvalue = useCallback(() => {
        dispatch(fetchApplicationWithId({ accessToken: authToken, id: params.id }));
        dispatch(getstate(authToken));
        dispatch(fetchCategories({ accessToken: authToken }));
    }, [dispatch, authToken, params.id]);

    useEffect(() => {
        setIsLoaded(false);
        setIsDataPopulated(false);
        statusvalue();
    }, [statusvalue]);

    const handleOpenReject = (item) => {
        setSelectedRejectItem(item);
        setRejectReason('');
        setShowRejectModal(true);
    };

    const handleCloseReject = () => {
        setShowRejectModal(false);
        setSelectedRejectItem(null);
        setRejectReason('');
    };

    const requestStatusChange = (statusName) => {
        setPendingStatusName(statusName);
        setShowStatusModal(true);
    };

    const handleCloseStatusModal = () => {
        setShowStatusModal(false);
        setPendingStatusName(null);
    };

    const handleConfirmStatusChange = (remarks = "") => {
        const statusName = pendingStatusName;
        handleCloseStatusModal();
        if (statusName === 'assign_to_company') {
            handleAssignToCompany(remarks);
            return;
        }
        if (statusName) {
            applySelectedStatus(statusName, remarks);
        }
    };

    const handleConfirmReject = (remarks = "") => {
        if (!selectedRejectItem || !remarks.trim()) return;
        const item = selectedRejectItem;
        handleCloseReject();
        handleChangeStatus({ ...item, remarks: remarks.trim() }, 5, null);
    };

    const handleChangeStatus = async (item, state, discrepancies = null) => {
        const result = await dispatch(
            updateApplication({
                accessToken: authToken,
                id: item?.id || params.id,
                state,
                descripencie: discrepancies,
                json_data: item?.json_data,
                remarks: item?.remarks || undefined,
            })
        );
        if (!isThunkFulfilled(result)) return;

        const template = getNotificationTemplate('rejected', {
            ...item,
            userName: item?.userName || item?.user_name || 'User',
            discrepancies,
        });

        dispatch(
            createNotification({
                message: template.message,
                userId: item?.user_id || item?.userId,
                accessToken: authToken,
                is_admin: false,
                subject: template.subject,
                htmlMailContent: template.html,
                sendToGamingBoardViewers: true,
                sendToGamingBoardReviewers: true,
                sendToCompanyAdmins: true,
            })
        );

        navigate('/rejected-applications');
    };

    // update application state
    const selecteddata = (e) => {
        if (e === 'rejected') {
            handleOpenReject(applicationdata);
            return;
        }
        requestStatusChange(e);
    };

    const applySelectedStatus = async (e, remarks = "") => {
        const filterstatus = status.filter((item) => item.name === e);
        const currentTime = new Date().toLocaleTimeString();

        const result = await dispatch(
            updateApplication({
                accessToken: authToken,
                id: params.id,
                state: filterstatus[0]?.id || '',
                currentTime,
                json_data: applicationdata?.json_data,
                remarks: remarks || undefined,
            })
        );
        if (!isThunkFulfilled(result)) return;

        const template = getNotificationTemplate(e, {
            ...applicationdata,
            userName: applicationdata?.userName || applicationdata?.user_name || 'User',
            discrepancies: applicationdata?.discrepancies
        });

        dispatch(
            createNotification({
                message: template.message,
                userId: applicationdata?.user_id,
                accessToken: authToken,
                is_admin: false,
                subject: template.subject,
                htmlMailContent: template.html,
                sendToGamingBoardViewers: true,
                sendToGamingBoardReviewers: true,
                sendToCompanyAdmins: true,
            })
        );

        const destination = getStatusRoute(e) || getStatusRoute(filterstatus?.[0]?.id);
        if (destination) {
            navigate(destination);
        } else {
            statusvalue();
        }
    };

    const handleAssignToCompany = async (remarks = "") => {
        const stateId = 20; // Assigned to Company
        const statusText = 'Assigned to Company';

        // Preserve additional_notes (may be string or { value }) along with field discrepancies
        const resolveNotes = (notes) => {
            if (typeof notes === 'string') return notes;
            if (notes?.value != null) return notes.value;
            return '';
        };
        const additionalNotes =
            resolveNotes(selectedFieldData?.additional_notes) ||
            resolveNotes(applicationdata?.discrepancies?.additional_notes);

        const { additional_notes: _ignored, ...fieldDiscrepancies } =
            selectedFieldData || {};

        const descripencie = {
            ...fieldDiscrepancies,
            additional_notes: additionalNotes,
        };

        const result = await dispatch(
            updateApplication({
                accessToken: authToken,
                id: params.id,
                state: stateId,
                descripencie,
                json_data: applicationdata?.json_data,
                remarks: remarks || undefined,
                navigate: '/discrepancies'
            })
        );
        if (!isThunkFulfilled(result)) return;

        const appName = applicationdata?.name || applicationdata?.applicationName || 'Application';
        const appId = applicationdata?.id || params.id;
        const subject = `Application Status Update: ${appName} (GB-${appId})`;
        const htmlContent = `
            <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
              <h2 style="color: #2c3e50;">Application Status Update</h2>
              <p>Dear User,</p>
              <p>Your application <strong>${appName}</strong> (ID: <strong>GB-${appId}</strong>) has been updated.</p>
              <p><strong>New Status:</strong> <span style="color: #007bff; font-weight: bold;">${statusText}</span></p>
              <p>Please log in to your dashboard to view more details.</p>
              <br>
              <p>Best regards,</p>
              <p><strong>Gaming Board Team</strong></p>
            </div>
        `;

        dispatch(createNotification({
            message: `Your application ${appName} (GB-${appId}) status has been updated to ${statusText}.`,
            userId: applicationdata?.user_id,
            subject: subject,
            htmlMailContent: htmlContent,
            sendToCompanyAdmins: true,
            accessToken: authToken,
            is_admin: false,
        }));

        statusvalue();
        navigate('/discrepancies');
    };

    // async setup when form is loaded
    useEffect(() => {
        let isMounted = true;
        const runAsync = async () => {
            if (isLoaded && applicationdata) {
                if (applicationdata.json_data) {
                    await setFormFieldData(applicationdata.json_data);
                }
                const userId = applicationdata.user_id;
                if (userId) {
                    try {
                        const signature = await getSignature({ userId, applicationId: params.id, authToken });
                        if (isMounted) setSignatureImage(signature);
                    } catch (err) {
                        console.error('Error fetching signature:', err);
                    }
                }
                if (isMounted) setIsDataPopulated(true);
            }
        };
        runAsync();
        return () => {
            isMounted = false;
        };
    }, [isLoaded, applicationdata, params.id, authToken]);

    // hide/show correct form page
    useEffect(() => {
        if (pageIds.length > 0) {
            hideAllPagesExceptCurrentFormPage(pageIds, currentPage);
        }
    }, [pageIds, currentPage]);

    // discrepancies prefill
    useEffect(() => {
        if (applicationdata?.discrepancies) {
            setSelectedFieldData(applicationdata.discrepancies);
        }
    }, [applicationdata]);

    // Apply red background to fields that are in discrepancies (on page load and when selectedFieldData changes)
    useEffect(() => {
        if (formIds.length > 0 && isLoaded) {
            // Apply red background to all fields that are in selectedFieldData
            Object.keys(selectedFieldData).forEach((discrepancyKey) => {
                // Remove 'dis_' prefix to get the actual field ID
                const fieldId = discrepancyKey.replace('dis_', '');
                const fieldElement = document.getElementById(fieldId);
                if (fieldElement) {
                    fieldElement.style.backgroundColor = '#ff0000';
                }
            });

            // Remove red background from fields that are not in selectedFieldData
            formIds.forEach((fieldId) => {
                const discrepancyKey = 'dis_' + fieldId;
                if (!selectedFieldData[discrepancyKey]) {
                    const fieldElement = document.getElementById(fieldId);
                    if (fieldElement) {
                        fieldElement.style.backgroundColor = '';
                    }
                }
            });
        }
    }, [selectedFieldData, formIds, isLoaded]);

    // click handler for discrepancies
    const handleInputChange = useCallback((event) => {
        if (hasRole(user, ROLES.VIEWER)) return; // Viewer cannot change discrepancies
        const fieldId = 'dis_' + event.target.id;
        setSelectedFieldData((prevFields) => {
            const newFields = { ...prevFields };
            if (newFields[fieldId]) {
                delete newFields[fieldId];
                event.target.style.backgroundColor = '';
            } else {
                newFields[fieldId] = { value: '', check: true };
                event.target.style.backgroundColor = '#ff0000';
            }
            return newFields;
        });
    }, [user]);

    // attach/remove listeners
    useEffect(() => {
        formIds.forEach((id) => {
            const inputField = document.getElementById(id);
            if (inputField) {
                inputField.removeEventListener('click', handleInputChange);
                inputField.addEventListener('click', handleInputChange);
            }
        });
        return () => {
            formIds.forEach((id) => {
                const inputField = document.getElementById(id);
                if (inputField) {
                    inputField.removeEventListener('click', handleInputChange);
                }
            });
        };
    }, [formIds, handleInputChange]);

    // inject signature
    useEffect(() => {
        if (formIds.length > 0 && signatureImage && isLoaded) {
            formIds.forEach((id) => {
                const inputField = document.getElementById(id);
                if (inputField?.type === 'image') {
                    inputField.src = signatureImage;
                }
            });
        }
    }, [signatureImage, isLoaded, formIds]);

    return (
        <div>
            <Navbar />
            <Header />
            <div className="wrapper">
                <div className="boxAppBoard boxWhite">
                    <div className="stickyFormHeader">
                        <div className="AppFormBar noShadow">
                            <div className="title">
                                {currentApp?.name} {formData.find((el) => el.id === currentPage)?.pageName}
                            </div>
                            <div className='d-flex align-items-center'>
                                <div className="pagination">
                                    <div className="pageNum d-flex align-items-center">
                                        <IoChevronBackSharp onClick={() => goToFormPreviousPage(currentPage, setCurrentPage)} />

                                        <input
                                            type="number"
                                            value={inputPage}
                                            style={{
                                                width: '50px',
                                                padding: '0px',
                                                background: '#191b1c',
                                                color: 'white',
                                                borderRadius: '5px',
                                                textAlign: 'center',
                                                appearance: 'none',
                                                borderWidth: 'medium',
                                                borderStyle: 'none',
                                                borderColor: 'currentcolor',
                                                borderImage: 'none',
                                            }}
                                            min="1"
                                            max={Number(pageIds.length)}
                                            onBeforeInput={(e) => {
                                                if (!/^\d$/.test(e.data)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setInputPage(value);
                                                if (value !== '') {
                                                    const pageNum = parseInt(value);
                                                    if (!isNaN(pageNum)) {
                                                        setPage(pageNum, currentPage, pageIds, setCurrentPage);
                                                    }
                                                }
                                            }}
                                            onFocus={(e) => e.target.select()}
                                            onMouseUp={(e) => e.preventDefault()}
                                        />

                                        <span className="slash">
                                            /<span style={{ marginLeft: 10 }}>{pageIds.length}</span>
                                        </span>
                                        <IoChevronForwardSharp onClick={() => goToFormNextPage(currentPage, pageIds, setCurrentPage)} />
                                    </div>
                                </div>
                                {
                                    !hasRole(user, ROLES.VIEWER) && (
                                        <div className="dropdown me-3">
                                            <button
                                                className="btn btn-primary"
                                                type="button"
                                                id="actionsDropdown"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                                style={{ height: '33px', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}
                                            >
                                                View Actions
                                            </button>
                                            <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="actionsDropdown">
                                                <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => requestStatusChange('assign_to_company')}
                                                    >
                                                        Assign to company
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>
                                    )
                                }

                            </div>
                        </div>
                    </div>

                    <div className="boardHeader">
                        <div className="boardHeadLeft">
                            <h2>Descrepancies</h2>
                           
                        </div>
                        <div className="boardHeadRight">
                            <button
                                type="button"
                                className="btn btn-outline-primary me-2"
                                onClick={() => setShowAuditTrail(true)}
                                style={{ height: '38px', whiteSpace: 'nowrap' }}
                            >
                                Audit Trail
                            </button>
                            <div>
                                <select
                                    className="form-select"
                                    value={applicationdata?.state || 'All'}
                                    disabled={hasRole(user, ROLES.VIEWER)}
                                    onChange={(e) => selecteddata(e.target.value)}
                                >
                                    <option disabled value="All">
                                        Select State
                                    </option>
                                    {status.length > 0 &&
                                        status
                                            .filter((item) =>
                                                ['in_process', 'discrepancy_GB_GB', 'approved', 'rejected'].includes(item.name)
                                            )
                                            .map((item) => (
                                                <option key={item.name} value={item.name}>
                                                    {item.name === 'in_process' ? 'In Process' : item.name === 'discrepancy_GB_GB' ? 'Discrepancies' : item.name === 'approved' ? 'Approved' : item.name === 'rejected' ? 'Rejected' : ''}
                                                </option>
                                            ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className={classes['board-content']}>
                        {(fetchLoading || !currentApp?.id || !isLoaded || !isDataPopulated) && (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}
                        {!fetchLoading && currentApp?.id && (
                            <div className="disarea" style={{ display: (isLoaded && isDataPopulated) ? 'flex' : 'none' }}>
                                <Index
                                    selectedFieldData={selectedFieldData}
                                    setSelectedFieldData={setSelectedFieldData}
                                    formId={params.id}
                                    formIds={formIds}
                                    currentPage={currentPage}
                                    setCurrentPage={setCurrentPage}
                                    applicationUserId={applicationdata?.user_id}
                                />
                                <FormLoader
                                    applicationName={applicationdata?.name}
                                    setFormIds={setFormIds}
                                    setPageIds={setPageIds}
                                    setIsLoaded={setIsLoaded}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <RejectDialog
                open={showRejectModal}
                reason={rejectReason}
                setReason={setRejectReason}
                onReject={handleConfirmReject}
                onCancel={handleCloseReject}
            />

            <StatusConfirmDialog
                open={showStatusModal}
                title="Are you sure you want to change status?"
                message={
                    pendingStatusName
                        ? `Change status to "${STATUS_LABELS[pendingStatusName] || pendingStatusName}"?`
                        : ""
                }
                onConfirm={handleConfirmStatusChange}
                onCancel={handleCloseStatusModal}
            />

            <AuditTrailModal
                open={showAuditTrail}
                onClose={() => setShowAuditTrail(false)}
                applicationId={params.id}
                applicationNo={params.id ? `GB-${params.id}` : ""}
                submittedBy={
                    applicationdata?.companyName ||
                    applicationdata?.company_name ||
                    applicationdata?.company?.company_name ||
                    "—"
                }
                submittedFor={
                    applicationdata?.userName ||
                    applicationdata?.user_name ||
                    "—"
                }
                accessToken={authToken}
            />
        </div>
    );
};

export default Descrepancies;
